<script setup lang="ts">
    import { useEvent } from '~/composables/useEventBus';

    defineProps<{
        entities: Array<{ type: string; id: string; title: string | null }>;
        previewEntity: { type: string; id: string } | null;
    }>();

    defineEmits<{
        'close-preview': [];
    }>();

    const togglePanel = () => useEvent('ai:toggle-context');
</script>

<template>
    <div class="flex flex-col h-full overflow-x-hidden bg-white">
        <!-- Entity preview takes over when an artifact is open -->
        <AiPageEntityPreview
            v-if="previewEntity"
            :type="previewEntity.type"
            :id="previewEntity.id"
            @close="$emit('close-preview')"
        />

        <template v-else>
            <!-- Header -->
            <div class="px-4 pt-[14px] pb-[10px] border-b border-[#E7E7E7] flex items-start justify-between gap-2">
                <div class="min-w-0">
                    <div class="flex items-center gap-2">
                        <i class="fa-solid fa-folder-open text-[12px] text-[#E8B102]" />
                        <span class="font-bricolage font-semibold text-[14px] text-[#0F1020]">
                            {{ $t('ai.context.title') }}
                        </span>
                    </div>
                    <div class="font-inter text-[11px] text-[#8D929C] mt-[3px]">
                        <template v-if="entities.length === 0">
                            {{ $t('ai.context.empty_inline') }}
                        </template>
                        <template v-else-if="entities.length === 1">
                            1 {{ $t('ai.context.linked_singular') }}
                        </template>
                        <template v-else>
                            {{ entities.length }} {{ $t('ai.context.linked_plural') }}
                        </template>
                    </div>
                </div>
                <button
                    type="button"
                    class="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#42484E] hover:bg-[#F3F3F3] transition-colors cursor-pointer flex-shrink-0"
                    :aria-label="$t('actions.close')"
                    @click="togglePanel"
                >
                    <i class="fa-solid fa-xmark text-[14px]" />
                </button>
            </div>

            <!-- List / empty state -->
            <div class="flex-1 overflow-y-auto nice-scrollbar p-[10px]">
                <div
                    v-if="entities.length === 0"
                    class="px-4 py-10 text-center font-inter text-[12px] text-[#8D929C] leading-relaxed"
                >
                    <i class="fa-regular fa-folder-open text-[22px] text-[#ABB5C0] block mb-2" />
                    {{ $t('ai.context.no_entities') }}
                </div>

                <AiPageEntityCard
                    v-for="entity in entities"
                    :key="`${entity.type}-${entity.id}`"
                    :entity="entity"
                />
            </div>
        </template>
    </div>
</template>
